import type { ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { cn } from "@/lib/utils";
import { LoadingState } from "./LoadingState";

/**
 * Card shell for paginated or filtered result lists (loading / error / empty / rows).
 *
 * Pair with `MasterDetailShell` for list|detail admin layouts (support chats, transcripts).
 *
 * **Do not use** when another component already owns those states:
 * - `DataTable` — presets, admin tables, device/reader inventory (uses `DataTableShell`)
 * - Static accordion / FAQ content — use `EmptyState` inline unless a card wrapper is desired
 * - Single-purpose forms inside `Card` (preset create forms above a `DataTable`)
 */
type ListResultsCardBaseProps = {
  hideHeader?: boolean;
  title?: ReactNode;
  headerAction?: ReactNode;
  /** Renders between header and content (filters, stats bars, alerts). */
  beforeContent?: ReactNode;
  isLoading?: boolean;
  loadingMessage?: string;
  /** When true and not loading/error, renders emptyState instead of children. */
  isEmpty?: boolean;
  emptyState?: ReactNode;
  children?: ReactNode;
  pagination?: ReactNode;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
};

type ListResultsCardErrorProps = ListResultsCardBaseProps & {
  isError: boolean;
  errorState: ReactNode;
};

type ListResultsCardNonErrorProps = ListResultsCardBaseProps & {
  isError?: false;
  errorState?: never;
};

export type ListResultsCardProps =
  | ListResultsCardErrorProps
  | ListResultsCardNonErrorProps;

export function ListResultsCard({
  hideHeader = false,
  title,
  headerAction,
  beforeContent,
  isLoading = false,
  loadingMessage,
  isError = false,
  errorState,
  isEmpty = false,
  emptyState,
  children,
  pagination,
  className,
  headerClassName,
  contentClassName,
}: ListResultsCardProps) {
  let body: ReactNode;
  if (isLoading) {
    body = <LoadingState message={loadingMessage} />;
  } else if (isError) {
    body = errorState;
  } else if (isEmpty && emptyState) {
    body = emptyState;
  } else {
    body = children;
  }

  return (
    <Card className={className}>
      {hideHeader ? null : (
        <CardHeader className={cn("border-b border-surface-200/70", headerClassName)}>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>{title}</CardTitle>
            {headerAction}
          </div>
        </CardHeader>
      )}
      {beforeContent}
      <CardContent className={cn("p-4", contentClassName)}>
        {body}
        {pagination}
      </CardContent>
    </Card>
  );
}
